/**
 * Module Access Hooks
 *
 * Hooks for checking module enablement status and controlling component access
 */

import { useSelector } from 'react-redux';
import { selectIsModuleEnabled, selectEnabledModules } from '../../app/slices/enabledModulesSlice';

/**
 * Hook to check if a module is enabled for current tenant
 *
 * @param {string} moduleKey - Module identifier (e.g., 'risk', 'compliance')
 * @returns {boolean} - True if module is enabled, false otherwise
 *
 * @example
 * const isRiskEnabled = useModuleEnabled('risk');
 * if (!isRiskEnabled) return <NotAvailable />;
 */
export const useModuleEnabled = (moduleKey) => {
  const isEnabled = useSelector(selectIsModuleEnabled(moduleKey));
  return isEnabled;
};

/**
 * Hook that requires a module to be enabled
 * Throws error if module is not enabled (for error boundary handling)
 *
 * @param {string} moduleKey - Module identifier
 * @returns {boolean} - Always true if module is enabled
 * @throws {Error} - If module is not enabled
 *
 * @example
 * export default function AuditPage() {
 *   useRequireModule('audit'); // Throws if audit not enabled
 *   return <AuditDashboard />;
 * }
 */
export const useRequireModule = (moduleKey) => {
  const isEnabled = useModuleEnabled(moduleKey);

  if (!isEnabled) {
    throw new Error(
      `Module '${moduleKey}' is not enabled for this tenant. ` +
        'Enable it in settings to access this page.'
    );
  }

  return true;
};

/**
 * Hook to get multiple modules' status at once
 *
 * @param {string[]} moduleKeys - Array of module identifiers
 * @returns {Object} - Object with module key as key, boolean as value
 *
 * @example
 * const modules = useModulesEnabled(['risk', 'audit']);
 * return (
 *   <div>
 *     {modules.risk && <RiskSection />}
 *     {modules.audit && <AuditSection />}
 *   </div>
 * );
 */
export const useModulesEnabled = (moduleKeys = []) => {
  const enabledModules = useSelector(selectEnabledModules);
  const enabled = {};

  for (const key of moduleKeys) {
    enabled[key] = enabledModules.includes(key);
  }

  return enabled;
};

/**
 * Hook to get list of all enabled modules
 *
 * @returns {string[]} - Array of enabled module keys
 *
 * @example
 * const enabledModules = useEnabledModuleList();
 * console.warn(enabledModules); // ['risk', 'compliance', 'audit']
 */
export const useEnabledModuleList = () => {
  return useSelector(selectEnabledModules);
};

/**
 * Hook to conditionally render based on module enablement
 *
 * @param {string} moduleKey - Module identifier
 * @param {React.ReactNode} children - Content to render if enabled
 * @param {React.ReactNode} fallback - Content to render if not enabled (optional)
 * @returns {React.ReactNode}
 *
 * @example
 * <ModuleContent moduleKey="audit">
 *   <AuditPanel />
 *   <NotAvailable fallback={<p>Audit module not enabled</p>} />
 * </ModuleContent>
 */
export const useShowIfModuleEnabled = (moduleKey, children, fallback = null) => {
  const isEnabled = useModuleEnabled(moduleKey);
  return isEnabled ? children : fallback;
};
